import { Loader2, SparklesIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Button } from '@/components/ui/button'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'

export type EplanExtractionReadiness =
  | 'none'
  | 'uploading'
  | 'processing'
  | 'failed'
  | 'ready'

type ExtractFromEplanButtonProps = {
  readiness: EplanExtractionReadiness
  eplanSettingsHref: string | null
  disabled: boolean
  isPending: boolean
  onClick: () => void
  size?: 'default' | 'sm'
}

function ExtractFromEplanButtonContent({ isPending }: { isPending: boolean }) {
  const { t } = useTranslation('machineryDirectiveCompliance')

  return (
    <>
      {isPending ? (
        <Loader2 className='mr-2 size-4 animate-spin' />
      ) : (
        <SparklesIcon className='mr-2 size-4' />
      )}
      {t('safetyFunctionsPage.extractFromEplan')}
    </>
  )
}

export function ExtractFromEplanButton({
  readiness,
  eplanSettingsHref,
  disabled,
  isPending,
  onClick,
  size,
}: ExtractFromEplanButtonProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const showSettingsLink =
    eplanSettingsHref != null &&
    (readiness === 'none' || readiness === 'failed')

  let hoverMessage = t('safetyFunctionsPage.extractFromEplanHover.processing')
  if (readiness === 'none') {
    hoverMessage = t('safetyFunctionsPage.extractFromEplanHover.noEplan')
  }
  if (readiness === 'failed') {
    hoverMessage = t('safetyFunctionsPage.extractFromEplanHover.failed')
  }

  const button = (
    <Button
      variant='outline'
      size={size}
      onClick={onClick}
      disabled={disabled}
    >
      <ExtractFromEplanButtonContent isPending={isPending} />
    </Button>
  )

  if (readiness === 'ready') {
    return button
  }

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <span className='inline-flex'>{button}</span>
      </HoverCardTrigger>
      <HoverCardContent className='w-72 space-y-3' side='top' align='start'>
        <p className='text-sm text-popover-foreground'>{hoverMessage}</p>
        {showSettingsLink ? (
          <Button variant='outline' size='sm' asChild>
            <Link to={eplanSettingsHref}>
              {t('safetyFunctionsPage.extractFromEplanHover.goToStudioSettings')}
            </Link>
          </Button>
        ) : null}
      </HoverCardContent>
    </HoverCard>
  )
}
